import { createAsyncThunk } from "@reduxjs/toolkit";
import LocalStorageService, { LocalStorageKeys } from "../../../services/localStorageService";
import api from "../../../api";
import { User } from "./types";

export const login = createAsyncThunk(
  "api/login",
  async (
    credentials: { email: string; password: string; keepMeSignedIn?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post("/api/auth/admin-login", {
        email: credentials.email,
        password: credentials.password,
      });
      LocalStorageService.set<boolean>(LocalStorageKeys.KEEP_ME_SIGNED_IN, !!credentials.keepMeSignedIn);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ message: "An unexpected error occurred" });
    }
  }
);

export const signup = createAsyncThunk(
  "api/signup",
  async (
    data: { full_name: string; email: string; password: string; },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post("/api/auth/web-signup", {
        full_name: data.full_name,
        email: data.email,
        password: data.password,
      });
      return response.data;
    } catch (error: any) {
        return rejectWithValue(error?.response?.data || { message: "An unexpected error occurred" });
    }
  }
);

export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async (data: { email: string; otp: string }, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/auth/verify-otp", {
        email: data.email,
        otp: data.otp,
      });
      const { accessToken, refreshToken, user } = response.data.data;
      LocalStorageService.set<string>(LocalStorageKeys.ACCESS_TOKEN, accessToken);
      LocalStorageService.set<string>(LocalStorageKeys.REFRESH_TOKEN, refreshToken);
      LocalStorageService.set(LocalStorageKeys.USER, user);
      sessionStorage.setItem("session-active", "true");
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ message: "An unexpected error occurred" });
    }
  }
);

export const refreshAccessToken = createAsyncThunk(
  "auth/refreshAccessToken",
  async (data: { refreshToken: string }, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/auth/refresh-token", {
        refreshToken: data.refreshToken,
      });
      const { accessToken, refreshToken: newRefreshToken } = response.data.tokens;
      LocalStorageService.set<string>(LocalStorageKeys.ACCESS_TOKEN, accessToken);
      LocalStorageService.set<string>(LocalStorageKeys.REFRESH_TOKEN, newRefreshToken);
      return accessToken;
    } catch (error: any) {
        return rejectWithValue(error.response.data || { message: "Refresh failed" });
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (data: { email: string }, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/auth/forgot-sub-password", {
        email: data.email,
      });
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ message: "An unexpected error occurred" });
    }
  }
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async ( data: { currentPassword: string; newPassword: string }, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/users/change-password", {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ message: "An unexpected error occurred" });
    }
  }
);

export const resendOtp = createAsyncThunk(
  "auth/resendOtp",
  async (data: { email: string }, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/auth/resend-otp", {
        email: data.email,
      });
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ message: "An unexpected error occurred" });
    }
  }
);

export const resetSubAdminPassword = createAsyncThunk(
  "auth/resetSubAdminPassword",
  async (data: { id: number; password: string }, { rejectWithValue }) => {
    try {
      const response = await api.put("/api/auth/reset-subadmin-password", data);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ message: "An unexpected error occurred" });
    }
  }
);

export const checkAuth = createAsyncThunk("auth/checkAuth", async () => {
  const keepMeSignedIn = LocalStorageService.get<boolean>(LocalStorageKeys.KEEP_ME_SIGNED_IN);
  const token = LocalStorageService.get<string>(LocalStorageKeys.ACCESS_TOKEN);
  const sessionActive = sessionStorage.getItem("session-active");

  if (token) {
    if (keepMeSignedIn || sessionActive) {
      const user = LocalStorageService.get<User>(LocalStorageKeys.USER);
      if (user) {
        return { user, logout: false }; // Valid session
      }
    }
    return { user: null, logout: true }; // Expired session
  }
  return { user: null, logout: false }; // Already logged out
});
